// MixerPopover — speech-bubble popover anchored at the MixerButton.
//
// Layout:
//   1. Auto-Duck row — toggle + strength slider + numeric readout. The
//      duck applies to the Video element AND every loaded Music/SFX bed
//      simultaneously (same edge transitions as the rAF duck loop).
//   2. Vertical channel strips, conditionally rendered:
//        Voice (always)
//        Video (only when current asset has a loaded video — local blob
//               OR admin-shipped videoPath)
//        SFX 1 (only when sfxBeds[currentAssetIdx][0] exists)
//        SFX 2 (only when sfxBeds[currentAssetIdx][1] exists)
//      Hiding faders for absent sources keeps the popover compact and
//      makes "what does this slider do?" unambiguous.
//   3. Speech-bubble arrow pointing down at the button.
//
// All mixer state lives in `useMixerStore`. Live application:
//   - Video fader → <video>.volume via useVideoDucking().applyVideoVolume()
//   - SFX faders → bed gain nodes via useVideoDucking().applySfxVolume()
//                  (delegates to applyBedGainExternalAll across all tracks)
//   - Voice fader → next-`new Audio()` via getEffectiveVoiceVolume()
//                   (preview pipeline routes through ensureVoiceGain).
//
// Legacy reference: index.html:2268–2304 (markup), 6297–6376 (state
// mutators), 6425–6447 (popover toggle + outside-click listener).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { scrollParentOf } from '@/lib/scrollParent';
import { useMixerStore, type SfxTrackIdx } from '@/stores/mixer';
import { applyPredefinedFreeVoiceFallback, useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { useVideoDucking } from '@/hooks/useVideoDucking';
import styles from './MixerPopover.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** DOM ref of the MixerButton — needed so the outside-click handler can
   *  ignore clicks that land back on the button (those are handled by the
   *  button's own onClick → toggle). */
  buttonRef: React.RefObject<HTMLElement | null>;
}

export function MixerPopover({ isOpen, onClose, buttonRef }: Props) {
  // ── Mixer store ────────────────────────────────────────────────────────
  const voiceVolume = useMixerStore((s) => s.voiceVolume);
  const voiceMuted = useMixerStore((s) => s.voiceMuted);
  const videoUserVolume = useMixerStore((s) => s.videoUserVolume);
  const videoMuted = useMixerStore((s) => s.videoMuted);
  const sfxVolumes = useMixerStore((s) => s.sfxVolumes);
  const sfxMuted = useMixerStore((s) => s.sfxMuted);
  const videoDuckingEnabled = useMixerStore((s) => s.videoDuckingEnabled);
  const videoDuckingStrength = useMixerStore((s) => s.videoDuckingStrength);

  const setVoiceVolume = useMixerStore((s) => s.setVoiceVolume);
  const setVoiceMuted = useMixerStore((s) => s.setVoiceMuted);
  const setVideoUserVolume = useMixerStore((s) => s.setVideoUserVolume);
  const setVideoMuted = useMixerStore((s) => s.setVideoMuted);
  const setSfxVolume = useMixerStore((s) => s.setSfxVolume);
  const setSfxMuted = useMixerStore((s) => s.setSfxMuted);
  const setDuckingEnabled = useMixerStore((s) => s.setDuckingEnabled);
  const setDuckingStrength = useMixerStore((s) => s.setDuckingStrength);

  // ── Source presence (drives dynamic strip visibility) ──────────────────
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const configAssets = useSessionStore((s) => s.config?.assets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const customAssets = useSessionStore((s) => s.customAssets);
  const sourceMap = useVideoStore((s) => s.sources);
  const blobMap = useVideoStore((s) => s.blobs);
  // Select PRIMITIVES from the store so Zustand's snapshot equality check
  // is stable. Returning an array via `s.sfxBeds[idx] ?? []` would yield a
  // fresh `[]` reference each call and trip useSyncExternalStore's
  // infinite-loop bailout ("getSnapshot should be cached to avoid an
  // infinite loop"). Booleans are safe.
  // Multi-Lingual (Spec 2026-09-21 § 6): Mixer ja (Voice, Video, Ducking),
  // SFX-Regler nein — dort gibt es keinen SfxTrack, über den je ein Bett
  // geladen werden könnte. Der Riegel hier hält den Mixer auch dann sauber,
  // wenn ein Bett aus einer älteren Session aus IndexedDB zurückkommt.
  const multiLingual = useSessionStore((s) => s.config?._simpleMode === true);
  const hasBed1 = useSessionStore(
    (s) => (s.sfxBeds[s.currentAssetIdx]?.length ?? 0) > 0,
  );
  const hasBed2 = useSessionStore(
    (s) => (s.sfxBeds[s.currentAssetIdx]?.length ?? 0) > 1,
  );
  const hasSfx1 = hasBed1 && !multiLingual;
  const hasSfx2 = hasBed2 && !multiLingual;

  const hasVideo = useMemo(() => {
    if (sourceMap[currentAssetIdx] || blobMap[currentAssetIdx]) return true;
    const cfgWithFallback = applyPredefinedFreeVoiceFallback(
      configAssets,
      configVoices,
    );
    const all = [...cfgWithFallback, ...customAssets];
    const asset = all[currentAssetIdx];
    return !!asset?.videoPath;
  }, [sourceMap, blobMap, currentAssetIdx, configAssets, configVoices, customAssets]);

  // ── Video / SFX live-apply hooks (back-compat) ─────────────────────────
  // useVideoDucking installs a module-level store subscription so slider
  // changes auto-propagate to the audio targets — these explicit pokes are
  // redundant but kept stable so the change set stays focused.
  const { applyVideoVolume, applySfxVolume } = useVideoDucking();

  // ── Outside-click close ────────────────────────────────────────────────
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // ── Richtung: nach oben (Standard) oder nach unten ─────────────────────
  // Der Button sitzt im sticky Timeline-Band. Haftet das Band oben am
  // Content-Pane, ragt ein nach oben geöffneter Popover aus dem Scroll-
  // Bereich und ist nicht erreichbar — dann öffnet er nach unten (Philip,
  // 2026-10-03: „position aware aufpoppen, dahin wo platz ist"). Gemessen
  // beim Öffnen und bei Scroll/Resize, solange offen.
  const [below, setBelow] = useState(false);
  useLayoutEffect(() => {
    if (!isOpen) return;
    const decide = () => {
      const btn = buttonRef.current;
      const pop = popoverRef.current;
      if (!btn || !pop) return;
      const btnRect = btn.getBoundingClientRect();
      const limitTop = scrollParentOf(btn)?.getBoundingClientRect().top ?? 0;
      const needed = pop.offsetHeight + 10 + 8;
      setBelow(btnRect.top - needed < limitTop);
    };
    decide();
    let raf = 0;
    const schedule = () => { if (!raf) raf = window.requestAnimationFrame(() => { raf = 0; decide(); }); };
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isOpen, buttonRef]);
  useEffect(() => {
    if (!isOpen) return;
    let removed = false;
    const onDocMouseDown = (ev: MouseEvent) => {
      const pop = popoverRef.current;
      const btn = buttonRef.current;
      if (!pop) return;
      if (pop.contains(ev.target as Node)) return;
      if (btn && btn.contains(ev.target as Node)) return;
      onClose();
    };
    const timer = window.setTimeout(() => {
      if (removed) return;
      document.addEventListener('mousedown', onDocMouseDown);
    }, 0);
    return () => {
      removed = true;
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [isOpen, onClose, buttonRef]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const onDuckToggle = useCallback(() => {
    setDuckingEnabled(!videoDuckingEnabled);
    // No imperative duck/unduck here — useVideoDucking's module-level
    // subscription on `videoDuckingEnabled` re-applies all targets with
    // the correct gain. Manually calling unduck() would clobber the
    // moduleIsDucked flag (which tracks "voice currently audible", not
    // "duck enabled") and prevent the duck from re-engaging when the
    // user toggles back on mid-block.
  }, [videoDuckingEnabled, setDuckingEnabled]);

  const onDuckStrengthChange = useCallback(
    (raw: string) => {
      const n = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
      setDuckingStrength(n);
      // Live-update is handled by the module-level subscription in
      // useVideoDucking — no explicit poke needed.
    },
    [setDuckingStrength],
  );

  const onVideoVolumeChange = useCallback(
    (raw: string) => {
      const v = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
      setVideoUserVolume(v / 100);
      if (v > 0 && videoMuted) setVideoMuted(false);
      applyVideoVolume();
    },
    [setVideoUserVolume, videoMuted, setVideoMuted, applyVideoVolume],
  );

  const onToggleVideoMute = useCallback(() => {
    setVideoMuted(!videoMuted);
    window.setTimeout(applyVideoVolume, 0);
  }, [videoMuted, setVideoMuted, applyVideoVolume]);

  const onVoiceVolumeChange = useCallback(
    (raw: string) => {
      const v = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
      setVoiceVolume(v / 100);
      if (v > 0 && voiceMuted) setVoiceMuted(false);
    },
    [setVoiceVolume, voiceMuted, setVoiceMuted],
  );

  const onToggleVoiceMute = useCallback(() => {
    setVoiceMuted(!voiceMuted);
  }, [voiceMuted, setVoiceMuted]);

  const onSfxVolumeChange = useCallback(
    (track: SfxTrackIdx, raw: string) => {
      const v = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
      setSfxVolume(track, v / 100);
      if (v > 0 && sfxMuted[track]) setSfxMuted(track, false);
      applySfxVolume();
    },
    [setSfxVolume, sfxMuted, setSfxMuted, applySfxVolume],
  );

  const onToggleSfxMute = useCallback(
    (track: SfxTrackIdx) => {
      setSfxMuted(track, !sfxMuted[track]);
      window.setTimeout(applySfxVolume, 0);
    },
    [sfxMuted, setSfxMuted, applySfxVolume],
  );

  // ── Derived UI bits ────────────────────────────────────────────────────
  const videoMuteIcon = videoMuted
    ? 'ph-fill ph-speaker-simple-x'
    : videoUserVolume < 0.5
      ? 'ph-fill ph-speaker-simple-low'
      : 'ph-fill ph-speaker-simple-high';
  const voiceMuteIcon = voiceMuted ? 'ph-fill ph-microphone-slash' : 'ph-fill ph-microphone';
  const sfxIconFor = (track: SfxTrackIdx) =>
    sfxMuted[track] ? 'ph-fill ph-music-notes-minus' : 'ph-fill ph-music-notes';

  const videoSlider = Math.round(videoUserVolume * 100);
  const voiceSlider = Math.round(voiceVolume * 100);

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className={`${styles.popover} ${below ? styles.popoverBelow : ''}`}
      role="dialog"
      aria-label="Audio Mixer"
    >
      <div className={styles.mixerHeader}>Audio Mixer</div>

      {/* Auto-Duck row */}
      <div className={styles.duckSection}>
        <button
          type="button"
          className={[styles.duckToggle, videoDuckingEnabled ? styles.duckToggleOn : '']
            .filter(Boolean)
            .join(' ')}
          onClick={onDuckToggle}
          title="Auto-duck video + Music/SFX while voice plays"
        >
          <i className="ph-fill ph-wave-sine" />
          <span>Auto-Duck</span>
        </button>
        <input
          type="range"
          className={styles.duckStrength}
          min={0}
          max={100}
          value={videoDuckingStrength}
          onChange={(e) => onDuckStrengthChange(e.target.value)}
          disabled={!videoDuckingEnabled}
          title="Ducking strength (0% = no duck, 100% = silence)"
        />
        <span className={styles.duckVal}>{videoDuckingStrength}%</span>
      </div>

      {/* Channel strips — only render strips for sources actually loaded
          on the active asset. Voice is always present. */}
      <div className={styles.channels}>
        {hasVideo && (
          <div className={styles.channel}>
            <div className={styles.vfaderWrap}>
              <input
                type="range"
                className={styles.vfader}
                min={0}
                max={100}
                value={videoSlider}
                onChange={(e) => onVideoVolumeChange(e.target.value)}
                title="Video volume"
              />
            </div>
            <button
              type="button"
              className={[styles.mute, videoMuted ? styles.muteMuted : '']
                .filter(Boolean)
                .join(' ')}
              onClick={onToggleVideoMute}
              title="Mute video"
            >
              <i className={videoMuteIcon} />
            </button>
            <div className={styles.channelLabel}>Video</div>
          </div>
        )}

        {hasSfx1 && (
          <div className={styles.channel}>
            <div className={styles.vfaderWrap}>
              <input
                type="range"
                className={styles.vfader}
                min={0}
                max={100}
                value={Math.round(sfxVolumes[0] * 100)}
                onChange={(e) => onSfxVolumeChange(0, e.target.value)}
                title="Music / SFX track 1 volume"
              />
            </div>
            <button
              type="button"
              className={[styles.mute, sfxMuted[0] ? styles.muteMuted : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => onToggleSfxMute(0)}
              title="Mute Music / SFX track 1"
            >
              <i className={sfxIconFor(0)} />
            </button>
            <div className={styles.channelLabel}>Track 1</div>
          </div>
        )}

        {hasSfx2 && (
          <div className={styles.channel}>
            <div className={styles.vfaderWrap}>
              <input
                type="range"
                className={styles.vfader}
                min={0}
                max={100}
                value={Math.round(sfxVolumes[1] * 100)}
                onChange={(e) => onSfxVolumeChange(1, e.target.value)}
                title="Music / SFX track 2 volume"
              />
            </div>
            <button
              type="button"
              className={[styles.mute, sfxMuted[1] ? styles.muteMuted : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => onToggleSfxMute(1)}
              title="Mute Music / SFX track 2"
            >
              <i className={sfxIconFor(1)} />
            </button>
            <div className={styles.channelLabel}>Track 2</div>
          </div>
        )}

        {/* Voice strip — always rendered last so it sits on the right
            (the channel strips read left-to-right Video → SFX → Voice,
            mirroring signal-flow downstream). */}
        <div className={styles.channel}>
          <div className={styles.vfaderWrap}>
            <input
              type="range"
              className={styles.vfader}
              min={0}
              max={100}
              value={voiceSlider}
              onChange={(e) => onVoiceVolumeChange(e.target.value)}
              title="Voice volume"
            />
          </div>
          <button
            type="button"
            className={[styles.mute, voiceMuted ? styles.muteMuted : '']
              .filter(Boolean)
              .join(' ')}
            onClick={onToggleVoiceMute}
            title="Mute voice"
          >
            <i className={voiceMuteIcon} />
          </button>
          <div className={styles.channelLabel}>Voice</div>
        </div>
      </div>

      {/* Speech-bubble arrow pointing at the button below */}
      <div className={styles.arrow} />
    </div>
  );
}
